export function debounce(func, wait) {
  let timeout
  const debounced = function (...args) {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), wait)
  }

  debounced.cancel = () => clearTimeout(timeout)

  return debounced
}

export function turboFetch(url, options) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  return fetch(url, {
    ...options,
    headers: {
      'Accept': 'text/vnd.turbo-stream.html',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
      ...options.headers
    },
    credentials: 'same-origin'
  })
    .then(response => response.text())
    .then(html => Turbo.renderStreamMessage(html))
}

export function turboFetchForm(url, formElement, options = {}) {
  const formData = new FormData(formElement)
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

  return fetch(url, {
    method: 'POST',
    body: formData,
    headers: {
      'Accept': 'text/vnd.turbo-stream.html',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
      ...options.headers
    },
    credentials: 'same-origin',
    ...options
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      return response.text()
    })
    .then(html => Turbo.renderStreamMessage(html))
}

export function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
}

export function fetchWithCsrf(url, options = {}) {
  const csrfToken = getCsrfToken()

  return fetch(url, {
    ...options,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
      ...options.headers
    },
    credentials: 'same-origin'
  })
}

export async function fetchJsonWithCsrf(url, method, params = undefined) {
  const response = await fetchWithCsrf(url, jsonRequestOptions(method, params))

  const payload = await parseJsonResponse(response)

  if (!response.ok) {
    throw new Error(errorMessageFromJsonPayload(payload, response))
  }

  return payload
}

function jsonRequestOptions(method, params) {
  const requestBody = jsonRequestBody(params)

  return {
    method,
    body: requestBody,
    headers: jsonRequestHeaders(requestBody)
  }
}

function jsonRequestBody(params) {
  if (params === undefined) {
    return undefined
  }

  return JSON.stringify(params)
}

function jsonRequestHeaders(requestBody) {
  const headers = { Accept: 'application/json' }

  if (requestBody) {
    headers['Content-Type'] = 'application/json'
  }

  return headers
}

async function parseJsonResponse(response) {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

function errorMessageFromJsonPayload(payload, response) {
  if (payload.error) {
    return payload.error
  }

  if (Array.isArray(payload.errors) && payload.errors.length > 0) {
    return payload.errors.join(', ')
  }

  return `Request failed with status ${response.status}`
}

export function setupTurboRedirect() {
  addEventListener("turbo:before-stream-render", ((event) => {
    const fallbackToDefaultActions = event.detail.render

    event.detail.render = function (streamElement) {
      if (streamElement.action == "redirect") {
        window.location.href = streamElement.getAttribute('location')
      } else {
        fallbackToDefaultActions(streamElement)
      }
    }
  }))
}

export function setupTimezoneCookie() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const oneYear = 60 * 60 * 60 * 24 * 365 * 1000

  let expiresAt = new Date()
  expiresAt.setTime(expiresAt.getTime() + oneYear)
  expiresAt = expiresAt.toGMTString()

  document.cookie = `timezone=${timezone}; expires=${expiresAt}; Path=/; SameSite=Lax; Secure`
}

export const pluralize = (count, singular, plural) => {
  return `${count} ${count === 1 ? singular : plural}`
}

const HTML_ESCAPE_MAP = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;"
}

export const escapeHtml = (text) => {
  if (text === null || text === undefined) return ""

  return String(text).replace(/[&<>"']/g, character => HTML_ESCAPE_MAP[character])
}

export const DEFAULT_FILENAME = "downloaded_file"

const MAX_FILENAME_LENGTH = 255
const PATH_SEPARATORS = /[/\\]/
const DISALLOWED_FILENAME_CHARACTERS = /[^A-Za-z0-9._ ()',&-]/g
const LEADING_DOTS = /^\.+/
const LAST_CONTROL_CHARACTER_CODE = 31
const DELETE_CHARACTER_CODE = 127

export const sanitizeFilename = (value) => {
  const sanitized = withoutControlCharacters(basename(value))
    .replace(DISALLOWED_FILENAME_CHARACTERS, "_")
    .replace(LEADING_DOTS, "")
    .slice(0, MAX_FILENAME_LENGTH)

  return sanitized || DEFAULT_FILENAME
}

const basename = (value) => {
  const segments = String(value || "").split(PATH_SEPARATORS).filter(segment => segment.length > 0)

  return segments[segments.length - 1] || ""
}

const withoutControlCharacters = (value) => {
  return Array.from(value).filter(character => !isControlCharacter(character)).join("")
}

const isControlCharacter = (character) => {
  const code = character.charCodeAt(0)

  return code <= LAST_CONTROL_CHARACTER_CODE || code === DELETE_CHARACTER_CODE
}
